import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ThemeProvider, styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";
import BotaoWhats from "../../Components/BotaoWhats/BotaoWhats";
import InputMoney from "../../Components/InputMoney/InputMoney";
import {
  IRPF_CONFIG,
  formatCurrencyValue,
} from "../../config/irpfConfig";
import {
  calculateIrpf,
  getEducationDeductionLimit,
} from "../../lib/irpfCalculator";
import { Theme } from "../../Themes/theme";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const INITIAL_FORM = {
  deductionModel: "simples",
  income: "",
  socialSecurity: "",
  dependents: "",
  education: "",
  medicalExpenses: "",
  alimony: "",
  otherDeductions: "",
  withheldTax: "",
};

const Calculadora = () => {
  const [open, setOpen] = React.useState(false);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [form, setForm] = React.useState(INITIAL_FORM);

  const dependentsCount = Number(form.dependents) || 0;
  const educationLimit = getEducationDeductionLimit(dependentsCount);

  const result = React.useMemo(
    () =>
      calculateIrpf({
        income: form.income,
        deductionModel: form.deductionModel,
        socialSecurity: form.socialSecurity,
        dependents: form.dependents,
        education: form.education,
        medicalExpenses: form.medicalExpenses,
        alimony: form.alimony,
        otherDeductions: form.otherDeductions,
        withheldTax: form.withheldTax,
      }),
    [form]
  );

  const handleMoneyChange = (field) => (value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleDependentsChange = (event) => {
    const { value } = event.target;
    if (value === "" || /^[0-9\b]+$/.test(value)) {
      setForm((current) => ({ ...current, dependents: value }));
    }
  };

  const handleEducationChange = (value) => {
    const safeValue = Number(value) || 0;
    if (safeValue > educationLimit) {
      setOpenAlert(true);
      setForm((current) => ({
        ...current,
        education: String(educationLimit.toFixed(2)),
      }));
      return;
    }

    setForm((current) => ({ ...current, education: value }));
  };

  const handleDeductionModelChange = (event) => {
    setForm((current) => ({
      ...current,
      deductionModel: event.target.value,
    }));
  };

  const rows = IRPF_CONFIG.annualBrackets;

  return (
    <ThemeProvider theme={Theme}>
      <Box
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          alignContent: "center",
          marginRight: { xs: "0px", sm: "64px", md: "156px", lg: "256px" },
          marginLeft: { xs: "0px", sm: "64px", md: "156px", lg: "256px" },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "28px" }}>
            CALCULADORA
          </Typography>
        </Box>

        <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
          Simule o imposto devido na {IRPF_CONFIG.declarationLabel}, usando a
          tabela anual do exercício {IRPF_CONFIG.declarationYear} para rendimentos
          do ano-calendário {IRPF_CONFIG.calendarYear}.
        </Typography>

        <Alert severity="info" sx={{ width: "100%", alignItems: "center" }}>
          <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
            A calculadora faz uma <strong>estimativa</strong> de imposto a pagar
            ou restituir. Para o preenchimento definitivo da declaração, use os
            informes oficiais de rendimentos e deduções.
          </Typography>
        </Alert>

        <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
          <strong>1º passo:</strong> informe seus rendimentos e deduções.
        </Typography>

        <Paper
          sx={{
            padding: "24px",
            display: "flex",
            width: { xs: "calc(100% - 48px)", lg: "800px" },
            flexDirection: "column",
            gap: "24px",
            alignSelf: "center",
          }}
        >
          <FormControl sx={{ width: { xs: "100%", md: "440px" } }}>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              Rendimentos tributáveis:
            </Typography>
            <InputMoney
              value={form.income}
              setValue={handleMoneyChange("income")}
              name={"Rendimentos tributáveis"}
            />
          </FormControl>

          <Divider />

          <FormControl>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              Modelo de dedução:
              <Tooltip
                sx={{ padding: 0, mx: 1 }}
                title={`A simplificada aplica 20% de desconto, limitada a ${formatCurrencyValue(
                  IRPF_CONFIG.deductions.simplifiedDeductionLimit
                )}.`}
                arrow
                enterTouchDelay={0}
              >
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Typography>
            <RadioGroup
              sx={{ display: "flex", flexDirection: "row", alignContent: "center" }}
              value={form.deductionModel}
              onChange={handleDeductionModelChange}
            >
              <FormControlLabel value="simples" control={<Radio />} label="Simplificada" />
              <FormControlLabel value="completa" control={<Radio />} label="Completa" />
            </RadioGroup>
          </FormControl>

          {form.deductionModel === "completa" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", md: "440px" },
                gap: 2,
              }}
            >
              <InputMoney
                value={form.socialSecurity}
                setValue={handleMoneyChange("socialSecurity")}
                name={"Previdência oficial"}
              />

              <Box>
                <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: "rgba(0, 0, 0, 0.7)" }}>
                  Número de dependentes:
                  <Tooltip
                    sx={{ padding: 0, mx: 1 }}
                    title={`Dedução anual de ${formatCurrencyValue(
                      IRPF_CONFIG.deductions.dependentDeduction
                    )} por dependente.`}
                    arrow
                    enterTouchDelay={0}
                  >
                    <IconButton>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <TextField
                  sx={{ width: "100%" }}
                  variant="outlined"
                  value={form.dependents}
                  onChange={handleDependentsChange}
                />
              </Box>

              <InputMoney
                value={form.education}
                setValue={handleEducationChange}
                name={"Despesa com instrução"}
              />

              <Typography sx={{ fontSize: { xs: "12px", md: "13px" }, color: "rgba(0, 0, 0, 0.7)" }}>
                Limite informado para educação: {formatCurrencyValue(educationLimit)}.
              </Typography>

              <InputMoney
                value={form.medicalExpenses}
                setValue={handleMoneyChange("medicalExpenses")}
                name={"Despesas médicas"}
              />

              <InputMoney
                value={form.alimony}
                setValue={handleMoneyChange("alimony")}
                name={"Pensão alimentícia"}
              />

              <InputMoney
                value={form.otherDeductions}
                setValue={handleMoneyChange("otherDeductions")}
                name={"Outras deduções"}
              />
            </Box>
          )}

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              Total de deduções:
            </Typography>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              R$ {result.formatted.deductions}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              Base de cálculo:
            </Typography>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" } }}>
              <strong>R$ {result.formatted.baseCalculation}</strong>
            </Typography>
          </Box>
        </Paper>

        <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
          <strong>2º passo:</strong> identifique a faixa aplicável.
        </Typography>

        <TableContainer sx={{ maxWidth: { xs: "100%", md: "848px" }, alignSelf: "center" }} component={Paper}>
          <Table sx={{ minWidth: { xs: "560px", md: "800px" } }} aria-label="Tabela anual IRPF">
            <TableHead sx={{ backgroundColor: "#00259c" }}>
              <TableRow>
                <TableCell sx={{ color: "white" }}>
                  <strong>BASE DE CÁLCULO</strong>
                </TableCell>
                <TableCell sx={{ color: "white" }} align="center">
                  <strong>ALÍQUOTA</strong>
                </TableCell>
                <TableCell sx={{ color: "white" }} align="center">
                  <strong>DEDUÇÃO</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isSelected = row.baseLabel === result.bracket.baseLabel;
                return (
                  <TableRow
                    key={row.baseLabel}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      backgroundColor: isSelected ? "#E5F6FD" : "",
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row.baseLabel}
                    </TableCell>
                    <TableCell align="center">{row.rateLabel}</TableCell>
                    <TableCell align="center">{row.deductionLabel}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
          <strong>3º passo:</strong> veja o imposto apurado antes do IRRF.
        </Typography>

        <Paper
          sx={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: { xs: "calc(100% - 48px)", lg: "800px" },
            alignSelf: "center",
          }}
        >
          <Typography sx={{ fontSize: { xs: "10px", md: "12px" }, color: "rgba(0, 0, 0, 0.7)" }}>
            (base de cálculo x alíquota) - dedução
          </Typography>
          <Typography sx={{ fontSize: { xs: "13px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
            R$ {result.formatted.baseCalculation} x {result.bracket.rate}% - R${" "}
            {result.formatted.deductionValue}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              <strong>Imposto a pagar:</strong>
            </Typography>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              <strong>R$ {result.formatted.totalTax}</strong>
            </Typography>
          </Box>
        </Paper>

        <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "rgba(0, 0, 0, 0.7)" }}>
          <strong>4º passo:</strong> informe o IRRF pago ao longo do ano.
        </Typography>

        <Paper
          sx={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: { xs: "calc(100% - 48px)", lg: "800px" },
            alignSelf: "center",
          }}
        >
          <InputMoney
            value={form.withheldTax}
            setValue={handleMoneyChange("withheldTax")}
            name={"IRRF"}
          />
        </Paper>

        <Fab
          sx={{ width: { xs: "100%", md: "50%" }, alignSelf: "center" }}
          onClick={() => setOpen(true)}
          variant="extended"
          color="primary"
          aria-label="finalizar cálculo"
        >
          FINALIZAR CÁLCULO
        </Fab>
      </Box>

      <Dialog open={openAlert} onClose={() => setOpenAlert(false)}>
        <DialogTitle sx={{ backgroundColor: "#FDF0F0" }}>
          <Alert severity="error">
            <AlertTitle>
              <strong>ATENÇÃO</strong>
            </AlertTitle>
            A dedução de despesa com instrução não pode ser superior a{" "}
            <strong>{formatCurrencyValue(educationLimit)}</strong>, considerando
            titular e dependentes.
          </Alert>
        </DialogTitle>
        <DialogActions sx={{ backgroundColor: "#FDF0F0", display: "flex", justifyContent: "center" }}>
          <Button onClick={() => setOpenAlert(false)}>Ok</Button>
        </DialogActions>
      </Dialog>

      <BootstrapDialog onClose={() => setOpen(false)} fullWidth open={open}>
        <BootstrapDialogTitle id="resultado-calculadora" onClose={() => setOpen(false)}>
          RESULTADO
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Paper
            sx={{
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              width: { xs: "calc(100% - 48px)" },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
                Imposto apurado
              </Typography>
              <Typography sx={{ color: "red", fontSize: { xs: "14px", md: "16px" } }}>
                <strong>R$ {result.formatted.totalTax}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
                IRRF pago
              </Typography>
              <Typography sx={{ color: "green", fontSize: { xs: "14px", md: "16px" } }}>
                <strong>R$ {result.formatted.withheldTax}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
              <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
                TOTAL {result.totalDue > 0 ? "A PAGAR" : "A RESTITUIR"}:
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "14px", md: "16px" },
                  color: result.totalDue > 0 ? "red" : "green",
                }}
              >
                <strong>R$ {result.formatted.totalDue}</strong>
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <BotaoWhats />
        </DialogActions>
      </BootstrapDialog>
    </ThemeProvider>
  );
};

export default Calculadora;
